// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Coding } from '@medplum/fhirtypes';

/** Provider role code for practitioner matching */
export type ProviderRoleCode = 'physician' | 'nurse-practitioner' | 'care-coordinator' | 'software-engineer';

/** Appointment delivery mode */
export type AppointmentMode = 'virtual' | 'in-person' | 'either';

/** Patient status category */
export type PatientStatusCategory = 'new' | 'existing';

/** Parsed appointment type from FHIR CodeSystem properties */
export interface AppointmentTypeDefinition {
  /** The FHIR Coding for this appointment type */
  coding: Coding;
  /** Human-readable display name */
  display: string;
  /** Default duration in minutes */
  durationMinutes: number;
  /** Recommended CPT billing code (e.g., "99202") */
  billingCode: string;
  /** Recommended CPT billing modifier (e.g., "95") */
  billingModifier: string;
  /** CPT/HCPCS options clinicians may select in the encounter note. */
  billingCodes?: string[];
  /** Patient-facing estimate category, independent of the final encounter CPT. */
  estimateCode?: 'intake' | 'check-in' | 'follow-up' | '';
  /** Provider role codes eligible for this appointment type */
  providerTypes: ProviderRoleCode[];
  /** Preferred provider type (if any) */
  preferredProvider: ProviderRoleCode | undefined;
  /** Patient status category */
  patientStatus: PatientStatusCategory;
  /** Description of which patients qualify */
  patientEligibility: string;
  /** Appointment delivery mode */
  mode: AppointmentMode;
  /** Whether this appointment type is visible in the patient portal for self-scheduling */
  visibleInPortal: boolean;
  /** CMS place of service code (e.g., "02" for Telehealth, "11" for Office) */
  placeOfServiceCode: string;
  /** Whether this concept is an active schedulable appointment type. */
  schedulable?: boolean;
}

/** System URL for the Polaris appointment types CodeSystem */
export const APPOINTMENT_TYPE_SYSTEM = 'http://polarissleep.com/appointment-types';

/** ValueSet URL for binding to CodingInput */
export const APPOINTMENT_TYPE_VALUESET = 'http://polarissleep.com/appointment-types/vs';

/** Cooldown buffer in minutes added after each appointment slot */
export const COOLDOWN_BUFFER_MINUTES = 15;

/** System URL for Polaris provider role codes */
export const POLARIS_PROVIDER_ROLE_SYSTEM = 'http://polarissleep.com/roles';

/** Polaris provider role coding objects */
export const POLARIS_PROVIDER_ROLES = {
  physician: {
    system: POLARIS_PROVIDER_ROLE_SYSTEM,
    code: 'physician',
    display: 'Physician (MD)',
  },
  'nurse-practitioner': {
    system: POLARIS_PROVIDER_ROLE_SYSTEM,
    code: 'nurse-practitioner',
    display: 'Nurse Practitioner (NP)',
  },
  'care-coordinator': {
    system: POLARIS_PROVIDER_ROLE_SYSTEM,
    code: 'care-coordinator',
    display: 'Care Coordinator (RN)',
  },
  'software-engineer': {
    system: POLARIS_PROVIDER_ROLE_SYSTEM,
    code: 'software-engineer',
    display: 'Software Engineer',
  },
} as const;

export const CANONICAL_APPOINTMENT_TYPE_CODES = ['new-patient-intake', 'check-in', 'follow-up'] as const;
export type CanonicalAppointmentTypeCode = (typeof CANONICAL_APPOINTMENT_TYPE_CODES)[number];

export const LEGACY_APPOINTMENT_TYPE_CODES = [
  'hsat-troubleshooting',
  'new-patient-results',
  'dme-followup-30',
  'dme-followup-60-plus',
  'dme-followup-90',
  'dme-followup-180',
  'misc-patient-followup',
  'existing-patient-results',
  'dme-setup-troubleshooting',
  'rpm-checkin',
] as const;

export const APPOINTMENT_TYPE_LEGACY_CODE_MAP: Record<string, CanonicalAppointmentTypeCode> = {
  'new-patient-intake': 'new-patient-intake',
  'hsat-troubleshooting': 'check-in',
  'new-patient-results': 'follow-up',
  'dme-followup-30': 'follow-up',
  'dme-followup-60-plus': 'follow-up',
  'dme-followup-90': 'follow-up',
  'dme-followup-180': 'follow-up',
  'misc-patient-followup': 'follow-up',
  'existing-patient-results': 'follow-up',
  'dme-setup-troubleshooting': 'check-in',
  'rpm-checkin': 'check-in',
};
